<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useSettingsStore } from "@/stores/storeSettings";
import { useMatch3Game } from "@/utils/gameMatches.js";
import { useQuizMatchesStore } from "@/stores/storeQuizMatches";

const storeQuizMatches = useQuizMatchesStore();
const storeSettings = useSettingsStore();

const gameBlockCanvas = ref(null);

const isMobile = computed(() => (storeSettings.widthWindow || 1024) <= 620);
const isSmallMobile = computed(() => (storeSettings.widthWindow || 1024) <= 360);

const getTileSize = computed(() => {
	// В Telegram miniapp всегда использовать мобильный размер
	if (storeSettings.isTelegramPage) return 50;
	if (isSmallMobile.value) return 38;
	if (isMobile.value) return 50;
	return 80;
});

const { score, newGame, showHint, endGame, resize } = useMatch3Game(gameBlockCanvas, {
	cols: 7,
	rows: 9,
	tileSize: getTileSize.value,
	gap: 1,
	tileKeys: ["tile_green_1", "tile_green_2", "tile_green_3", "tile_green_4"],
	onEndGame: (finalScore) => {
		console.log("Игра завершена, очки:", finalScore);
	},
	onGameReady: () => {
		// Применить правильный размер тайлов после инициализации игры
		resize(getTileSize.value);
	},
});

watch(score, (newScore) => {
	storeQuizMatches.updateScore(newScore);
}, { immediate: true });

const handleResize = () => {
	resize(getTileSize.value);
};

onMounted(() => {
	if (typeof window !== "undefined") {
		window.addEventListener("resize", handleResize);
	}
});
onUnmounted(() => {
	
	if (typeof window !== "undefined") {
		window.removeEventListener("resize", handleResize);
	}
});
</script>
<template>
	<div class="match3 quiz-padding">
		<div class="container">
			<div class="match3__container">
				<h2 class="match3__title game-title">Игра «3 в ряд»</h2>
				<div class="match3__header">
					<div class="match3__control --score">
						<span>Счёт:</span>
						<span>{{ score }}</span>
					</div>
					<div class="match3__control --timer">
						<svg width="32" height="32"><use href="#timer"></use></svg>
						<span>{{ storeQuizMatches.remainingTime.minutes }}:{{ storeQuizMatches.remainingTime.secundes }}</span>
					</div>
				</div>
				<div ref="gameBlockCanvas" class="match3__canvas"></div>
			</div>
		</div>
	</div>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";
.game-container {
}

.match3 {
	padding: 4.3rem 0 7.4rem;
	background-image: url("@/assets/img/games/shape_1.webp"), url("@/assets/img/games/shape_2.webp");
	background-repeat: no-repeat;
	background-position:
		0% 20%,
		100% 80%;
	background-size: 16rem, 40.1rem;
	@media (max-width: vars.$m) {
		background-image: none;
	}
	@media (max-width: vars.$x480) {
		padding: 2.7rem 0 7.4rem;
	}
	&__container {
		display: grid;
		gap: 2.5rem;
		align-items: start;
		justify-content: center;
		justify-items: center;
		@media (max-width: vars.$x480) {
			gap: 2rem;
		}
	}

	&__title {
		line-height: 100%;
	}

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 39rem;
		@media (max-width: vars.$xx) {
			width: 97%;
		}
	}

	&__control {
		display: flex;
		align-items: center;

		font-family: var(--second-family);
		font-weight: 700;
		font-size: 2.8rem;
		line-height: 110%;
		text-transform: uppercase;
		color: var(--text-inverse-100);
		gap: 5px;

		@media (max-width: vars.$x480) {
			font-size: 2rem;
		}
		&.--score {
			span {
				&:first-child {
					color: var(--text-accent-2-100);
				}
			}
		}
		&.--timer {
			// width: 12rem;
			justify-content: end;
			svg {
				width: 3.2rem;
				height: 3.2rem;
				stroke: transparent;
				@media (max-width: vars.$x480) {
					width: 2rem;
					height: 2rem;
				}
			}
			span {
				width: 8.5rem;
				@media (max-width: vars.$x480) {
					width: 75px;
				}
				@media (max-width: vars.$x425) {
					width:68px;
				}
				@media (max-width: vars.$x393) {
					width:65px;
				}
			}
		}
	}

	&__canvas {
		position: relative;
		border: 0.1rem solid var(--border-accent-2-1);
		border-radius: 1.6rem;
		overflow: hidden;
		width: fit-content;
		cursor: pointer;
		touch-action: none;
		-webkit-touch-callout: none;
		-webkit-user-select: none;
		user-select: none;
		@media (max-width: vars.$x480) {
			border-radius: 1.2rem;
		}
		&::before {
			position: absolute;
			content: "";
			inset: 0;
			z-index: 1;
			pointer-events: none;
			box-shadow:
				inset 0 13px 35px 0 rgba(255, 255, 255, 0.14),
				0 16px 33px 0 rgba(0, 0, 0, 0.25);
		}
	}
}

.v-enter-active,
.v-leave-active {
	transition: all 0.25s ease-out;
}

.v-enter-from {
	opacity: 0;
	transform: translate(-50%, 10px);
}
.v-leave-to {
	opacity: 0;
	transform: translate(-50%, 10px);
}

.body-enter-active,
.body-leave-active {
	transition: 0.5s ease;
}

.body-enter-from,
.body-leave-to {
	opacity: 0;
}
</style>
